/**
 * PauseMenuComponent.ts
 *
 * Custom web component voor het pauze-menu.
 * Toont een responsive overlay met knoppen: Unpause, Restart Level en Back to Menu.
 * Knoppen gebruiken nu de GameEventService (exact zoals de Menu-knop in CanvasComponent),
 * zodat ze 100% betrouwbaar werken en consistent zijn met de rest van de app.
 * Geen CustomEvent meer nodig → geen bubble/composed issues.
 */
import { GameEventService } from "../services/GameEventService";
import { Page } from "../enums/Page";

export class PauseMenuComponent extends HTMLElement {
    /** GameEventService om pagina’s te switchen (zelfde als CanvasComponent) */
    private readonly _gameEventService: GameEventService = new GameEventService();

    public constructor() {
        super();
        this.render();
    }

    private render(): void {
        this.innerHTML = `
            <style>
                /* pause menu container: full screen overlay met flexbox */
                .pause-menu {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100vh;
                    width: 100vw;
                    background-color: rgba(0, 0, 0, 0.85);
                    color: white;
                    font-family: sans-serif;
                    position: fixed;
                    top: 0;
                    left: 0;
                    z-index: 9999;
                }

                .pause-title {
                    font-size: 3rem;
                    margin-bottom: 3rem;
                    text-align: center;
                }

                .pause-buttons {
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                    width: 80%;
                    max-width: 400px;
                }

                .pause-button {
                    padding: 1.2rem 2rem;
                    font-size: 1.5rem;
                    background-color: #444;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .pause-button:hover {
                    background-color: #666;
                }

                /* responsive voor telefoon en tablet */
                @media (max-width: 600px) {
                    .pause-title {
                        font-size: 2rem;
                    }
                    .pause-button {
                        font-size: 1.2rem;
                        padding: 1rem 1.5rem;
                    }
                }
            </style>

            <div class="pause-menu">
                <h1 class="pause-title">Paused</h1>
                <div class="pause-buttons">
                    <button class="pause-button" id="unpause">Resume</button>
                    <button class="pause-button" id="back-to-menu">Back to Menu</button>
                </div>
            </div>
        `;

        this.attachListeners();
    }

    private attachListeners(): void {
        const unpauseBtn: HTMLButtonElement = this.querySelector("#unpause") as HTMLButtonElement;
        const backBtn: HTMLButtonElement = this.querySelector("#back-to-menu") as HTMLButtonElement;

        unpauseBtn.addEventListener("click", () => {
            this._gameEventService.switchPage(Page.Canvas);
        });

        backBtn.addEventListener("click", () => {
            /* Back to Menu = LevelSelector (het hoofdmenu van de game) */
            this._gameEventService.switchPage(Page.Startup);
        });
    }

    public connectedCallback(): void {
        /* listeners opnieuw koppelen als het component opnieuw in de DOM komt */
        this.attachListeners();
    }
}
